// Отделы компании (/departments) — структура компании (дерево департаментов).
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { http } from '@/shared/api/http';
import { env } from '@/shared/config';
import { fetchDepartmentsV2 } from './api/departments-v2';

/**
 * ID отдела: число на legacy-бэке (Bitrix), UUID на v2. Страница отдела,
 * аналитика и фильтры передают его как есть — бэк каждого режима понимает свой.
 */
export type DepartmentId = number | string;

// 08.08.2026 бэк проставил json-теги и развернул sql.Null*: раньше приходило
// { ID, Name: {String,Valid}, ParentID: {Int64,Valid} }, теперь плоский camelCase.
export interface Department {
  id: DepartmentId;
  name: string;
  parentID?: DepartmentId;
}

export interface DepartmentListResponse {
  result: Department[];
}

export const fetchDepartments = async (): Promise<DepartmentListResponse> => {
  // На v2 legacy-маршрута /departments нет: он удалён вместе с Bitrix-слоем.
  if (env.useV2) {
    const list = await fetchDepartmentsV2();
    return {
      result: list
        .filter((d) => Boolean(d.id))
        .map((d) => ({
          id: d.id as string,
          name: d.name ?? '',
          parentID: d.parentId || undefined,
        })),
    };
  }
  const { data } = await http.get<{ result?: Department[] }>('/departments');
  return { result: data?.result ?? [] };
};

export const useDepartments = () =>
  useQuery({
    queryKey: ['departments', env.useV2 ? 'v2' : 'legacy'],
    queryFn: fetchDepartments,
    staleTime: 10 * 60 * 1000,
  });

/** Число на legacy, непустая строка (UUID) на v2. */
export function isDepartmentId(id: unknown): id is DepartmentId {
  if (typeof id === 'number') {
    return Number.isFinite(id);
  }
  return typeof id === 'string' && id.trim() !== '';
}

/**
 * Отделы (/departments) в форме { ID, Name } — для дропдаунов выбора отдела.
 * Замена useUserGroups (он отдаёт отфильтрованные рабочие группы пользователя,
 * а нужен полный список отделов компании). Совместима по форме с useUserGroups.
 */
export function useDepartmentGroups() {
  const { data, isLoading } = useDepartments();
  const groups = useMemo(
    () =>
      (data?.result ?? [])
        .map((d) => ({ ID: d.id, Name: (d.name ?? '').trim() }))
        .filter((g) => isDepartmentId(g.ID) && Boolean(g.Name)),
    [data]
  );
  return { groups, isLoading };
}

/** ID отдела из пути /groups/:id — число на legacy, UUID на v2. */
export const DEPARTMENT_PATH_RE = /^\/groups\/([^/?#]+)/;

/** ID отдела из URL в том виде, который ждёт бэк текущего режима. */
export function toDepartmentId(raw: string): DepartmentId {
  return env.useV2 ? raw : Number(raw);
}
